import errorIcon from "@assets/images/business-error.svg";
import accuracyIcon from "@assets/images/model-accuracy.svg";
import { ClientContext } from "@context/Client/ClientContext";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Skeleton,
  Theme,
  Typography,
  useTheme
} from "@mui/material";
import { useClientModelKpis } from "@services/api";
import { ClientUIMMMModelMetric } from "@services/api/types";
import {
  getModelAccuracyColor,
  getModelBIColor,
  getModelMAPEColor
} from "@utils/model_kpis";
import { toLocaleString } from "@utils/numbers";
import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

interface KpiCardProps {
  label: string;
  sublabel?: string;
  value?: number;
  isLoading: boolean;
  borderColor: string;
}

const KpiCard = ({
  label,
  sublabel,
  value,
  isLoading,
  borderColor
}: KpiCardProps) => {
  const theme = useTheme();

  return (
    <React.Fragment>
      {isLoading ? (
        <Skeleton variant="rectangular" className="w-full m-4" />
      ) : (
        <Card
          className="w-full m-4"
          sx={{ borderTopColor: borderColor, borderTopWidth: 4 }}
        >
          <CardContent>
            <Box className="flex items-center pl-2">
              <Box className="flex flex-row">
                <Typography variant="h6" component="div">
                  {label}
                </Typography>
                <Typography
                  variant="body1"
                  component="div"
                  className="pl-2 pt-1"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  {`${sublabel ? "· " : ""}${sublabel || ""}`}
                </Typography>
              </Box>
            </Box>
            <Grid container justifyContent="center" className="pl-2">
              <Grid
                size={{ xs: 1, md: 1, lg: 1 }}
                sx={{
                  display: "flex",
                  alignItems: "center"
                }}
              >
                <CircularProgress
                  enableTrackSlot
                  variant="determinate"
                  value={value || 0}
                  aria-label="Kpi value"
                  sx={{ color: borderColor }}
                />
              </Grid>
              <Grid
                size={{ xs: 11, md: 11, lg: 11 }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "left"
                }}
              >
                <Typography variant="h4" component="div" className="pl-2">
                  {value ? `${toLocaleString(value)}%` : "-"}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </React.Fragment>
  );
};

const ModelKpis = () => {
  const { currentClient } = useContext(ClientContext);
  const { t } = useTranslation();
  const theme = useTheme();

  const { data: kpis, isFetching: isLoading } = useClientModelKpis(
    currentClient.model.id
  );

  const KPI_CONFIG_MAPPING: Record<
    ClientUIMMMModelMetric,
    {
      icon: string;
      label: string;
      borderFn: (value: number, theme: Theme) => string;
      sublabel?: string;
    }
  > = useMemo(
    () => ({
      ["model_accuracy"]: {
        icon: accuracyIcon,
        label: "mmmodel.kpis.model_accuracy",
        borderFn: getModelAccuracyColor,
        sublabel: "mmmodel.kpis.model_accuracy_sublabel"
      },
      ["mape"]: {
        icon: errorIcon,
        label: "mmmodel.kpis.mape",
        borderFn: getModelMAPEColor,
        sublabel: "mmmodel.kpis.mape_sublabel"
      },
      ["business_error"]: {
        icon: errorIcon,
        label: "mmmodel.kpis.business_error",
        borderFn: getModelBIColor,
        sublabel: undefined
      }
    }),
    [theme]
  );

  const modelAccuracy = useMemo(() => {
    return kpis?.model_accuracy;
  }, [kpis]);

  const modelMape = useMemo(() => {
    return kpis?.mape;
  }, [kpis]);

  const modelBusinessError = useMemo(() => {
    return kpis?.business_error;
  }, [kpis]);

  const kpisCards = useMemo(() => {
    return currentClient.client.mmm_model_config.metrics.map((metric) => {
      const metricValue =
        metric === "model_accuracy"
          ? modelAccuracy
          : metric === "mape"
            ? modelMape
            : modelBusinessError;

      return (
        <Grid
          size={{
            xs: 12,
            md: 12 / currentClient.client.mmm_model_config.metrics.length,
            sm: 6
          }}
          sx={{ display: "flex", justifyContent: "center" }}
          key={metric}
        >
          <KpiCard
            label={t(KPI_CONFIG_MAPPING[metric].label)}
            sublabel={
              KPI_CONFIG_MAPPING[metric].sublabel
                ? t(KPI_CONFIG_MAPPING[metric].sublabel)
                : undefined
            }
            borderColor={KPI_CONFIG_MAPPING[metric].borderFn(
              metricValue || 0,
              theme
            )}
            value={metricValue}
            isLoading={isLoading}
          />
        </Grid>
      );
    });
  }, [
    currentClient.client.mmm_model_config.metrics,
    modelAccuracy,
    modelMape,
    modelBusinessError,
    isLoading
  ]);

  return (
    <Grid container justifyContent="center" className="w-full">
      {kpisCards}
    </Grid>
  );
};

export default ModelKpis;
